import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { API, api, fmtErr, imgSrc, uploadImage } from "./api";

export const GalleryTab = () => {
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = () =>
        axios.get(`${API}/gallery`).then((r) => setPhotos(r.data)).catch(() => toast.error("Could not load gallery"));
    useEffect(() => {
        load();
    }, []);

    const addPhotos = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const url = await uploadImage(f);
                await api().post("/admin/gallery", { url, caption: f.name });
            }
            toast.success("Photos added to your website gallery!");
            load();
        } catch (er) {
            toast.error(fmtErr(er));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const remove = async (p) => {
        if (!window.confirm("Delete this photo from the website gallery?")) return;
        try {
            await api().delete(`/admin/gallery/${p.id}`);
            toast.success("Photo deleted");
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        }
    };

    return (
        <div data-testid="gallery-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-heading font-bold text-2xl text-navy tracking-tight">Photo Gallery</h2>
                    <p className="text-sm text-muted mt-1">Factory, showroom and raw design photos shown on your website.</p>
                </div>
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="inline-flex items-center justify-center gap-2 bg-navy text-white px-8 py-4 text-base font-semibold hover:bg-gold transition-colors disabled:opacity-50"
                    data-testid="gallery-upload-button">
                    {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                    {uploading ? "Uploading..." : "Upload Photos"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} data-testid="gallery-file-input" />
            </div>

            {photos.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-line" data-testid="gallery-empty">
                    <p className="text-lg font-semibold text-navy">No photos yet</p>
                    <p className="text-sm text-muted mt-1">Upload factory or showroom photos — they appear on the website instantly.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gallery-grid">
                    {photos.map((p) => (
                        <div key={p.id} className="relative group border border-line" data-testid={`gallery-item-${p.id}`}>
                            <img src={imgSrc(p.url)} alt={p.caption || "Gallery photo"} className="h-40 w-full object-cover" />
                            <button onClick={() => remove(p)}
                                className="absolute top-2 right-2 bg-red-600 text-white p-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-gallery-${p.id}`} aria-label="Delete photo">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
